import {
  Active,
  DroppableContainer,
  SensorContext,
  type KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import { WINDOW_PROPERTY_SORT_POSITION } from "./utils";

const arrowKeys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"];

declare global {
  interface Window {
    [WINDOW_PROPERTY_SORT_POSITION]?: string | number;
  }
}

type Order = {
  zoneOrder: (string | number)[];
  zones: Record<string | number, (string | number)[]>;
  looseItems: (string | number)[];
};

const getNextCoordinates: KeyboardCoordinateGetter = (event, args) => {
  if (!arrowKeys.includes(event.code)) {
    return undefined;
  }

  if (
    !args.context.droppableContainers.get(window[WINDOW_PROPERTY_SORT_POSITION])
  ) {
    // If the window variable was polluted, reset it
    window[WINDOW_PROPERTY_SORT_POSITION] = undefined;
  }

  const zones = args.context.droppableContainers
    .values()
    .filter((value) => value.data.current?.isZone)
    .toArray();

  const order: Order = {
    zoneOrder: [],
    zones: {},
    looseItems: [],
  };
  order.zoneOrder = zones.map((zone) => {
    order.zones[zone.id] = zone.data.current?.sortedIds ?? [];
    return zone.id;
  });
  order.looseItems = args.context.droppableContainers
    .values()
    .filter((item) => !item.data.current?.isZone && !item.data.current?.zone)
    .map((item) => item.id)
    .toArray();

  const currentPositionDroppable = getDroppableOfId(
    window[WINDOW_PROPERTY_SORT_POSITION] ?? args.active, // current position ID or initial position ID
    args.context,
  );
  if (!currentPositionDroppable) return undefined;
  const currentZoneId = getZoneIdOfDroppable(currentPositionDroppable);

  if (event.code === "ArrowLeft" || event.code === "ArrowRight") {
    const newPositionId =
      event.code === "ArrowRight"
        ? getNextZoneIdFromZoneId(currentZoneId, order)
        : getPreviousZoneIdFromZoneId(currentZoneId, order);
    if (!newPositionId) return undefined;
    const newZone = getDroppableOfId(newPositionId, args.context);
    if (!newZone) return undefined;

    window[WINDOW_PROPERTY_SORT_POSITION] = newPositionId;
    return getCoordsFromZone(newZone, args.context.active);
  }

  if (event.code === "ArrowDown" || event.code === "ArrowUp") {
    const currentZone = getDroppableOfId(currentZoneId, args.context);
    if (!currentZone || !currentZone.data.current?.sortable) return undefined;

    if (currentPositionDroppable.data.current?.isZone) {
      // droppable is a zone, meaning we're at the default item position.
      // Up should go to the last item, down should go to the first item
      const zoneItems = currentZone.data.current?.sortedIds ?? [];
      const newPositionId =
        event.code === "ArrowDown"
          ? zoneItems[0]
          : zoneItems[zoneItems.length - 1];
      const newPosition = getDroppableOfId(newPositionId, args.context);
      if (!newPosition) return undefined;
      window[WINDOW_PROPERTY_SORT_POSITION] = newPositionId;
      return getCoordsFromItem(newPosition);
    } else {
      // droppable is an item, meaning we're at an index
      const currentItemId = currentPositionDroppable.id;
      const newPositionId =
        event.code === "ArrowDown"
          ? getNextItemId(currentItemId, order, args.active)
          : getPreviousItemId(currentItemId, order, args.active);
      if (!newPositionId) {
        // handle wrap by sending the position to the zone
        window[WINDOW_PROPERTY_SORT_POSITION] = currentZoneId;
        return getCoordsFromZone(currentZone, args.context.active);
      }
      const newPosition = getDroppableOfId(newPositionId, args.context);
      if (!newPosition) return undefined;
      window[WINDOW_PROPERTY_SORT_POSITION] = newPositionId;
      return getCoordsFromItem(newPosition);
    }
  }
  return undefined;
};

export default getNextCoordinates;

const getNextItemId = (
  id: string | number,
  order: Order,
  active: string | number,
): string | number | null => {
  if (order.looseItems.includes(id)) {
    return null;
  }
  const zoneId = order.zoneOrder.find((zoneId) =>
    order.zones[zoneId].includes(id),
  );
  if (!zoneId) return null;

  const zoneItems = order.zones[zoneId];
  const currentIndex = zoneItems.indexOf(id);
  const nextId = zoneItems[currentIndex + 1];
  if (nextId) {
    return nextId;
  }
  if (zoneItems.includes(active)) {
    return zoneItems[0];
  }
  return null;
};

const getPreviousItemId = (
  id: string | number,
  order: Order,
  active: string | number,
): string | number | null => {
  if (order.looseItems.includes(id)) {
    return null;
  }
  const zoneId = order.zoneOrder.find((zoneId) =>
    order.zones[zoneId].includes(id),
  );
  if (!zoneId) return null;
  const zoneItems = order.zones[zoneId];
  const currentIndex = zoneItems.indexOf(id);
  const previousIndex = currentIndex - 1;
  const previousId = zoneItems[previousIndex];
  if (previousId) {
    return previousId;
  }
  if (zoneItems.includes(active)) {
    return zoneItems[zoneItems.length - 1];
  }
  return null;
};

const getDroppableOfId = (
  id: string | number,
  context: SensorContext,
): DroppableContainer | null => {
  const droppable = context.droppableContainers.get(id);
  if (!droppable) {
    return null;
  }
  return droppable;
};

const getZoneIdOfDroppable = (droppable: DroppableContainer) => {
  if (droppable.data.current?.isZone) {
    return droppable.id;
  }
  if (droppable.data.current?.zone) {
    return droppable.data.current?.zone.id;
  }
  return droppable.id;
};

const getNextZoneIdFromZoneId = (
  zoneId: string | number,
  order: Order,
): string | number => {
  const currentIndex = order.zoneOrder.indexOf(zoneId);
  const nextIndex = (currentIndex + 1) % order.zoneOrder.length;
  const nextId = order.zoneOrder[nextIndex];
  return nextId;
};

const getPreviousZoneIdFromZoneId = (
  zoneId: string | number,
  order: Order,
): string | number => {
  const currentIndex = order.zoneOrder.indexOf(zoneId);
  const previousIndex =
    currentIndex === 0 ? order.zoneOrder.length - 1 : currentIndex - 1;
  const previousId = order.zoneOrder[previousIndex];
  return previousId;
};

export function getCoordsFromItem(item: DroppableContainer) {
  if (!item.rect.current) return;
  return {
    x: item.rect.current.left,
    y: item.rect.current.top,
  };
}

export function getCoordsFromZone(
  zone: DroppableContainer,
  active: Active | null,
) {
  // If the zone is the active zone, return the initial coordinates of the active item
  if (zone.id === active?.data.current?.zone?.id) {
    return {
      x: active.rect.current.initial?.left ?? 0,
      y: active.rect.current.initial?.top ?? 0,
    };
  }

  const zoneRight = zone.rect.current?.right ?? 0;
  const zoneBottom = zone.rect.current?.bottom ?? 0;
  const zoneLeft = zone.rect.current?.left ?? 0;
  const zoneTop = zone.rect.current?.top ?? 0;
  const zoneWidth = zone.rect.current?.width ?? 0;
  const zoneHeight = zone.rect.current?.height ?? 0;
  const zoneCenterX = zoneLeft + zoneWidth / 2;
  const zoneCenterY = zoneTop + zoneHeight / 2;

  const activeWidth = active?.rect.current?.initial?.width ?? 0;
  const activeHeight = active?.rect.current?.initial?.height ?? 0;
  const activeHalfWidth = activeWidth / 2;
  const activeHalfHeight = activeHeight / 2;

  if (!zone.data.current?.sortable || !zone.data.current?.sortedIds?.length) {
    return {
      x: zoneCenterX - activeHalfWidth,
      y: zoneCenterY - activeHalfHeight,
    };
  }

  const dropPosition = zone.data.current?.defaultDropPosition;
  const orientation = zone.data.current?.orientation;

  if (dropPosition === "end" && orientation === "horizontal") {
    return {
      x: zoneRight - activeHalfWidth,
      y: zoneCenterY - activeHalfHeight,
    };
  }
  if (dropPosition === "end" && orientation === "vertical") {
    return {
      x: zoneCenterX - activeHalfWidth,
      y: zoneBottom - activeHalfHeight,
    };
  }
  if (dropPosition === "start" && orientation === "horizontal") {
    return {
      x: zoneLeft - activeHalfWidth,
      y: zoneCenterY - activeHalfHeight,
    };
  }
  if (dropPosition === "start" && orientation === "vertical") {
    return {
      x: zoneCenterX - activeHalfWidth,
      y: zoneTop - activeHalfHeight,
    };
  }
}
